import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const InventoryAnalytics = ({ analyticsData }) => {
  // Generate turnover data (in production, this would come from actual sales data)
  const turnoverData = [
    { month: 'Jan', turnover: 2.4 },
    { month: 'Feb', turnover: 2.8 },
    { month: 'Mar', turnover: 3.2 },
    { month: 'Apr', turnover: 2.9 },
    { month: 'May', turnover: 3.5 },
    { month: 'Jun', turnover: 3.8 }
  ];

  // Use real category data from analytics
  const COLORS = ['#2563EB', '#059669', '#D97706', '#DC2626', '#64748B', '#8B5CF6', '#EC4899'];
  
  const categoryData = analyticsData?.categoryData?.map((cat, index) => ({
    name: cat.name,
    value: parseFloat(cat.value),
    count: cat.count,
    stockValue: cat.stockValue,
    color: COLORS[index % COLORS.length]
  })) || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const kpiCards = [
    {
      title: 'Total Stock Value',
      value: formatCurrency(analyticsData?.totalStockValue || 0),
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign'
    },
    {
      title: 'Items in Stock',
      value: (analyticsData?.totalItems || 0)?.toLocaleString(),
      change: '+8.2%',
      changeType: 'positive',
      icon: 'Package'
    },
    {
      title: 'Low Stock Alerts',
      value: (analyticsData?.lowStockItems || 0)?.toString(),
      change: analyticsData?.lowStockItems > 0 ? `${analyticsData?.lowStockItems} items` : 'All good',
      changeType: analyticsData?.lowStockItems > 0 ? 'negative' : 'positive',
      icon: 'AlertTriangle'
    },
    {
      title: 'Avg Turnover Rate',
      value: `${analyticsData?.avgTurnoverRate || 0}x`,
      change: '+5.7%',
      changeType: 'positive',
      icon: 'TrendingUp'
    }
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {kpiCards?.map((kpi, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={kpi?.icon} size={20} className="text-primary" />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                kpi?.changeType === 'positive' ?'bg-success/10 text-success' :'bg-error/10 text-error'
              }`}>
                {kpi?.change}
              </span>
            </div>
            <div className="space-y-1">
              <h3 className="text-2xl font-bold text-foreground">{kpi?.value}</h3>
              <p className="text-sm text-muted-foreground">{kpi?.title}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Inventory Turnover Chart */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">Inventory Turnover Rate</h3>
            <Icon name="BarChart3" size={20} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={turnoverData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="turnover" 
                  fill="var(--color-primary)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">Stock by Category</h3>
            <Icon name="PieChart" size={20} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {categoryData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name, props) => {
                    const data = props.payload;
                    return [
                      `${value}% (${data.count} items, ${formatCurrency(data.stockValue)})`,
                      data.name
                    ];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {categoryData?.map((category, index) => (
              <div key={index} className="flex items-center justify-between space-x-2 p-2 rounded hover:bg-muted/50 transition-colors">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: category?.color }}
                  />
                  <span className="text-xs text-muted-foreground">{category?.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-foreground">{category?.value}%</span>
                  <span className="text-xs text-muted-foreground">({category?.count})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Recent Activity & Pending Orders */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Recent Stock Movements */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">Recent Stock Movements</h3>
            <Icon name="Activity" size={20} className="text-muted-foreground" />
          </div>
          <div className="space-y-4">
            {analyticsData?.recentMovements?.map((movement, index) => (
              <div key={index} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    movement?.type === 'in' ? 'bg-success/10' : 'bg-error/10'
                  }`}>
                    <Icon 
                      name={movement?.type === 'in' ? 'ArrowUp' : 'ArrowDown'} 
                      size={14} 
                      className={movement?.type === 'in' ? 'text-success' : 'text-error'} 
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{movement?.item}</div>
                    <div className="text-xs text-muted-foreground">{movement?.timestamp}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${
                    movement?.type === 'in' ? 'text-success' : 'text-error'
                  }`}>
                    {movement?.type === 'in' ? '+' : '-'}{movement?.quantity}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Purchase Orders */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">Pending Purchase Orders</h3>
            <Icon name="ShoppingCart" size={20} className="text-muted-foreground" />
          </div>
          <div className="space-y-4">
            {analyticsData?.pendingOrders?.map((order, index) => (
              <div key={index} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
                    <Icon name="Clock" size={14} className="text-warning" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{order?.supplier}</div>
                    <div className="text-xs text-muted-foreground">{order?.items} items • {order?.expectedDate}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">{formatCurrency(order?.value)}</div>
                  <div className="text-xs text-muted-foreground">{order?.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryAnalytics;